import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import DropDownOA from '../../common/dropDownOA'
import { Item } from '../../common/snippets'
import { Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { monthNamesList } from '../GeneralSettings/ReportsPage/gsReportsConst'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { urls } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { useAtom, useSetAtom } from 'jotai'
import { GetRequest } from '../../common/api'
import { ReportHeading } from './common/ReportHeading'
import { CargoRoleRightsAtom } from '../../common/jotaiState/commonStateAtoms'
import { TableComponent } from '../../common/TableComponent'
import { monthlyTargetHeader } from '../dashBoard/constants'
const reportName = "Monthly Target Report"

const branchTypeGroups = [
    { key: 'own', label: 'Own' },
    { key: 'franchisee', label: 'Franchisee' },
]
const totalFields = ['TargetAmount', 'TillTodayTarget', 'Acheived', 'AchievementPercentage']
const amountFields = ['TargetAmount', 'TillTodayTarget', 'Acheived']

const getNumber = (value) => {
    const numberValue = Number(String(value ?? 0).replace(/,/g, '').replace('%', ''))
    return Number.isFinite(numberValue) ? numberValue : 0
}

const formatTotalValue = (field, value) => {
    if (field === 'AchievementPercentage') {
        return `${value.toFixed(2)}%`
    }
    return amountFields.includes(field) ? Math.round(value) : value
}

const formatEntryRow = (item, index) => ({
    ...item,
    rank: index + 1,
    dataType: 'entry',
    AchievementPercentage:
        item.AchievementPercentage || item.AchievementPercentage === 0
            ? `${getNumber(item.AchievementPercentage).toFixed(2)}%`
            : item.AchievementPercentage,
})

const getTotalRow = (label, rows, headerLength) => {
    const totals = totalFields.reduce((acc, field) => {
        const total = rows.reduce((sum, row) => sum + getNumber(row[field]), 0)
        acc[field] = formatTotalValue(field, total)
        return acc
    }, {})
    const percentage = totals.TillTodayTarget !== 0 ? (totals.Acheived / totals.TillTodayTarget) * 100 : 0;
    totals.AchievementPercentage = `${percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2)}%`;
    return {
        ...totals,
        rank: label,
        dataType: 'total',
        align: { rank: 'right' },
        colSpan: { rank: headerLength - totalFields.length },
    }
}

const formatData = (rows, headers = monthlyTargetHeader) => {
    const formattedRows = []
    const headerLength = headers.length
    const groupedEntryRows = []

    branchTypeGroups.forEach(({ key, label }) => {
        const groupRows = rows
            .filter((item) => String(item.CompanyType || '').trim().toLowerCase() === key).sort((a, b) => getNumber(b.AchievementPercentage) - getNumber(a.AchievementPercentage))
            .map(formatEntryRow)
        if (!groupRows.length) return

        groupedEntryRows.push(groupRows)
        formattedRows.push({
            rank: `Branch Type: ${label}`,
            dataType: 'subHeader',
            align: { rank: 'left' },
            colSpan: { rank: headerLength },
        })
        formattedRows.push(...groupRows)
        formattedRows.push(getTotalRow(`Total: Branch Type: ${label}`, groupRows, headerLength))
    })

    if (groupedEntryRows.length === branchTypeGroups.length) {
        formattedRows.push(getTotalRow('Total: Branch Type: Own + Franchisee', groupedEntryRows.flat(), headerLength))
    }

    return formattedRows
}

export const MonthlyTargetReport = () => {
    const { loginData } = useToken()
    const { intCompanyID, intBranchID } = loginData
    const setLoader = useSetAtom(globalLoaderAtom)
    const setAlert = useSetAtom(alertAtom)
    const currentYear = moment().year()
    const currentMonth = moment().month() + 1
    const [yearList, setYearList] = useState([])
    const [year, setYear] = useState({ year: currentYear })
    const [month, setMonth] = useState(monthNamesList[currentMonth - 1])
    const [tableData, setTableData] = useState([])
    const [branches, setBranches] = useState([])
    const [branch, setBranch] = useState({})
    const [roleRights] = useAtom(CargoRoleRightsAtom)
    const RoleRights = roleRights?.Table1?.[0] || {}
    const { DashboardAccessLevel } = RoleRights || {}

    const monthList = useMemo(() => {
        if (year?.year === currentYear) {
            return monthNamesList.filter((monthData) => monthData.id <= currentMonth).reverse()
        }
        return [...monthNamesList].reverse()
    }, [currentMonth, currentYear, year?.year])
    useEffect(() => {
        const years = []
        for (let i = currentYear; i > currentYear - 3; i--) {
            years.push({ year: i })
        }
        setYearList(years)
    }, [currentYear])
    useEffect(() => {
        if (!monthList.length) return
        if (!month || !monthList.some((monthData) => monthData.id === month.id)) {
            setMonth(monthList[0])
        }
    }, [month, monthList])
    const handleClick1 = () => {
        setLoader(true)
        const data = {
            CompanyID: intCompanyID,
            DateFrom: `${year?.year}-${month?.id.toString().padStart(2, '0')}-01`,
            BranchID: 0
        }
        GetRequest(urls.GetMonthlyWiseTarget, data)
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                if (!Array.isArray(res.Data.Table) || res.Data.Table.length === 0) {
                    throw new Error('No Data Found')
                }
                let list = res.Data.Table;
                if(list.length > 1){
                    list = [{BranchID:0, BranchName: 'All Branches'}, ...list]
                }
                const loginBranch = list.find((item) => item.BranchID === intBranchID)
                setBranch(loginBranch || {})
                setBranches(list)
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))

    }
    const handleClick2 = () => {
        if (!branch?.BranchName) {
            setAlert({ show: true, msg: 'Please select a branch', nextId: '' })
            return
        }
        setLoader(true)
        const data = {
            CompanyID: intCompanyID,
            DateFrom: `${year?.year}-${month?.id.toString().padStart(2, '0')}-01`,
            BranchID: branch?.BranchID || 0
        }
        GetRequest(urls.GetMonthlyWiseTarget, data)
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                if (!Array.isArray(res.Data.Table1) || res.Data.Table1.length === 0) {
                    throw new Error('No Data Found')
                }
                setTableData(formatData(res.Data.Table1))
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))

    }
    const criteriaHeaders = [{
        label: 'Year',
        field: 'year'
        , textAlign: 'left'
    }, {
        label: 'Month',
        field: 'year',
        textAlign: 'left'
    }, {
        label: 'Branch',
        field: 'branch',
        textAlign: 'right'
    }]
    const criteriaData = [{
        year: year?.year,
        month: month?.name,
        branch: branch?.BranchName
    }]
    return (
        <Item sx={{ padding: '0' }} className="reports">
            <ReportHeading reportName={reportName} />
            <Box sx={sx.reportFlex} style={{ background: '#fff', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                <Item sx={{ ...sx.itemsx }} className="dashboard-branch">
                    <DropDownOA
                        id='mtYear'
                        defaultvalue={year?.year}
                        label='Year'
                        prevId='mtMonthName'
                        nextId='mtBranchType'
                        data={yearList}
                        dataKey={`year`}
                        idKey={`year`}
                        onSelect={(val) => { setYear(val); }}
                    />
                </Item>

                <Item sx={{ ...sx.itemsx }} className="dashboard-branch">
                    <DropDownOA
                        id='mtMonthName'
                        defaultvalue={month?.name}
                        label='Month'
                        prevId=''
                        nextId='mtYear'
                        data={monthList}
                        dataKey={`name`}
                        idKey={`id`}
                        onSelect={(val) => { setMonth(val); }}
                    />
                </Item>
                <Item id="generateDiv" tabIndex={0} sx={sx.itemsx}>
                    <Button
                        id="showDashBoard"
                        variant="contained"
                        className="btn btn-primary"
                        autoFocus={false}
                        onClick={handleClick1}
                        sx={{
                            width: '100%',
                            letterSpacing: '2px',
                            height: '40px',
                        }}
                    >
                        GO
                    </Button>
                </Item>
                <Item sx={{ ...sx.itemsx }} className="dashboard-branch">
                    <Item sx={{ ...sx.itemsx }} className="dashboard-branch">
                        <DropDownOA
                            id="selectBranch"
                            label={'Select Branch'}
                            defaultvalue={branch?.BranchName || ''}
                            onSelect={(val) => {
                                setBranch(val)
                            }}
                            dataKey={'BranchName'}
                            idKey={'BranchID'}
                            data={branches}
                            width="100%"
                            listWidth={'280px'}
                            disabled={DashboardAccessLevel < 2}
                        />
                    </Item>
                </Item>
                <Item id="generateDiv" tabIndex={0} sx={sx.itemsx}>
                    <Button
                        id="showDashBoard"
                        variant="contained"
                        className="btn btn-primary"
                        autoFocus={false}
                        onClick={handleClick2}
                        sx={{
                            width: '100%',
                            letterSpacing: '2px',
                            height: '40px',
                        }}
                    >
                        Load
                    </Button>
                </Item>
            </Box>
            {tableData.length ? (
                <TableComponent
                    reportName={reportName}
                    criteriaHeaders={criteriaHeaders}
                    criteriaData={criteriaData}
                    tableData={tableData}
                    mainHeader={monthlyTargetHeader}
                />
            ) : (
                <></>
            )}
        </Item>
    )
}
